import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service } from 'webapp-types';
import { uniqBy } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CustomQuerySetupCommonDimensionSelectorComponent from 'webapp/components/custom-query/setup/common/dimension-selector/index';
import CustomQuerySetupCommonPreviewTableHeaderComponent from 'webapp/components/custom-query/setup/common/preview-table/header';
import CustomQuerySetupCommonPreviewTableRowComponent from 'webapp/components/custom-query/setup/common/preview-table/row';
import CustomQuerySetupPopupTransformAttributeComponent from 'webapp/components/custom-query/setup/popup-transform-attribute';
import FormRangeInlineComponent from 'webapp/components/form/range/inline';

import type CustomMetricSetupFormBase from 'webapp/models-custom/custom-metric-setup-form/base';
import DateRange from 'webapp/models-custom/date-range';
import FilterSortQueryPreview from 'webapp/models-custom/filter-sort/query-preview';

interface Args {
	form: CustomMetricSetupFormBase;
}

export class PreviewRow {
	data: APIData.CustomQuery.PreviewRow; // returned by BE

	dateValue: string | null; // for filter-sort
	attributeValue: string | null; // for filter-sort
	metricValue: string | null; // for filter-sort

	constructor(data: APIData.CustomQuery.PreviewRow, filterProps: SomeDataObject) {
		this.data = data;
		this.dateValue = filterProps.dateValue || null;
		this.attributeValue = filterProps.attributeValue || null;
		this.metricValue = filterProps.metricValue || null;
	}
}

export default class CustomQuerySetupCommonPreviewTableComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	@tracked filterSort: FilterSortQueryPreview | null = null;

	maxRows = 500;

	@cached
	get previewData() {
		return this.args.form.previewDataForBottomTable;
	}

	get previewDataHeaders(): APIData.CustomQuery.PreviewHeader[] {
		return this.previewData?.headers || [];
	}

	get previewDataRows(): APIData.CustomQuery.PreviewRow[] {
		return this.previewData?.rows || [];
	}

	get previewDataTotalRows(): APIData.CustomQuery.TotalRow[] | [] {
		return this.previewData?.totalRows || [];
	}

	get showPreviewControls(): boolean {
		return !!this.previewData || this.args.form.wasLastPreviewTimeout;
	}

	get disableDateRange() {
		return this.args.form.isLoadingPreview;
	}

	get rangePickerExceptionIds(): number[] {
		return [DateRange.Category.POPULAR];
	}

	get showTableRows(): boolean {
		return this.previewDataRows.length > 0 || this.previewDataTotalRows.length > 0;
	}

	get results() {
		return (this.filterSort?.results || []) as PreviewRow[];
	}

	@cached
	get allItems() {
		const items: PreviewRow[] = [];

		const headers = this.previewDataHeaders,
			dateHeader = headers.find((item) => item.isDate)!,
			metriceHeader = headers.find((item) => item.isMetric)!,
			attributeHeader = headers.find((item) => item.isAttribute)!,
			dateIndex = headers.indexOf(dateHeader),
			metricIndex = headers.indexOf(metriceHeader),
			attributeIndex = headers.indexOf(attributeHeader);

		this.previewDataRows.forEach((rowData: APIData.CustomQuery.PreviewRow, idx: number) => {
			if (idx < this.maxRows) {
				const row = new PreviewRow(rowData, {
					dateValue: rowData[dateIndex]?.current || null,
					attributeValue: rowData[attributeIndex]?.current || null,
					metricValue: rowData[metricIndex]?.current || null,
				});
				items.push(row);
			}
		});

		return items;
	}

	get isAttributed(): boolean {
		const headers = this.previewDataHeaders;
		return !!headers.find((item) => item.isAttribute);
	}

	get isTruncated(): boolean {
		return this.previewDataRows.length >= this.maxRows;
	}

	get showDimensionsWarning(): boolean {
		return this.isAttributed && this.previewDataTotalRows.length === this.recordLimit;
	}

	get recordLimit() {
		return this.args.form.previewBlock.recordLimit || 15;
	}

	get columnsCount(): number {
		return this.previewDataHeaders.length + 1;
	}

	@action
	changeDateRange(dateRangeCM: DateRange): void {
		const form = this.args.form;

		const limitations = form.metricLimitations;
		// only range part here
		limitations.updateDateRangeFromOnlyRangeFormula(dateRangeCM.rangeFormula);
		limitations.selectOptimalValidGranulation();
	}

	@action
	openAttributeTransform(
		props: Omit<PopupData.CustomQuery.TransformAttributes, 'possibleAttributes' | 'form'>,
	) {
		const data: PopupData.CustomQuery.TransformAttributes = Object.assign(
			{
				possibleAttributes: this.getPossibleAttributes(),
				form: this.args.form, // setting transforms directly on the form
			},
			props,
		);

		const popup = this.popupService.createModal(
			CustomQuerySetupPopupTransformAttributeComponent,
			{
				popupClass: 'transformpopup',
			},
		);

		popup.open(data);
	}

	@action
	setupView(): void {
		this.filterSort = new FilterSortQueryPreview({
			getAllItems: () => {
				return this.allItems;
			},
		});
	}

	@action
	teardownView(): void {
		this.filterSort = null;
	}

	private getPossibleAttributes() {
		const possibleAttributes: { originalName: string; current: string }[] = [];
		const attributeHeader = this.previewDataHeaders.find((item) => item.isAttribute)!,
			attributeIndex = this.previewDataHeaders.indexOf(attributeHeader);
		this.previewDataRows.forEach((row) => {
			const { current, originalName } = row[attributeIndex]!;

			if (originalName && current) {
				possibleAttributes.push({
					originalName: originalName,
					current: current as string,
				});
			}
		});

		return uniqBy(possibleAttributes, 'originalName');
	}
	<template>
		<div id="query-preview" {{didInsert this.setupView}} {{willDestroy this.teardownView}}>
			<div class="ct-title">
				<strong>Data preview</strong>

				{{#if this.showPreviewControls}}
					<FormRangeInlineComponent
						@labelText="Show data for"
						@metricLimitations={{@form.metricLimitations}}
						@exceptionIds={{this.rangePickerExceptionIds}}
						@addEm={{true}}
						@onChange={{this.changeDateRange}}
						@isDisabled={{this.disableDateRange}}
					/>

					{{#if @form.availableDimensions}}
						<CustomQuerySetupCommonDimensionSelectorComponent
							@form={{@form}}
							@isInline={{true}}
							@theme="dark"
						/>
					{{/if}}
				{{/if}}
			</div>

			{{#if @form.isLoadingPreview}}
				<div class="loading">
					<BarLoaderComponent />
					<p>Gathering data...</p>
				</div>
			{{else}}
				{{#if this.previewData}}
					{{#if this.showTableRows}}

						<table class="bigtable">
							<colgroup>
								<col width="7%" />
								<col width="31%" />
								<col width={{if this.isAttributed "31%" "62%"}} />
							</colgroup>
							<thead>
								<tr>
									<th>#</th>
									{{#if this.filterSort}}
										{{#each this.previewDataHeaders key="@index" as |header|}}
											<CustomQuerySetupCommonPreviewTableHeaderComponent
												@model={{header}}
												@filterSort={{this.filterSort}}
												@allowTransformations={{@form.allowTransformations}}
												@openAttributeTransform={{this.openAttributeTransform}}
												@dimensionName={{@form.selectedPreviewDimensionName}}
											/>
										{{/each}}
									{{/if}}
								</tr>
							</thead>
							<tbody>
								{{#each this.results as |row index|}}
									<CustomQuerySetupCommonPreviewTableRowComponent
										@model={{row}}
										@index={{index}}
									/>
								{{/each}}
								{{#if this.isTruncated}}
									<tr class="truncatedRow">
										<td colspan={{this.columnsCount}}>
											Displaying only the first
											{{this.maxRows}}
											results of data for this query.
										</td>
									</tr>
								{{/if}}
								{{#each this.previewDataTotalRows as |row index|}}
									<tr class="totalRow {{unless index 'totalRows-start'}}">
										<td>Ʃ</td>
										{{#each row key="@index" as |cell|}}
											<td>{{cell}}</td>
										{{/each}}
									</tr>
								{{/each}}
								{{#if this.showDimensionsWarning}}
									<tr class="truncatedRow">
										<td colspan={{this.columnsCount}}>
											Preview data is limited to a maximum of
											{{this.recordLimit}}
											dimensions per period.
										</td>
									</tr>
								{{/if}}
							</tbody>
						</table>
					{{else}}
						<div class="nodata">
							No results returned.
						</div>
					{{/if}}
				{{else}}
					<div class="nodata">
						Nothing to display here. Please preview data first.
					</div>
				{{/if}}
			{{/if}}
		</div>
	</template>
}
